import React, { useState, useEffect } from "react";
import { Button, Input, Checkbox, Alert, Tooltip } from "antd";
import { Link } from "react-router-dom";
import type { CheckboxChangeEvent } from "antd/es/checkbox";

import { useGoogleAuth } from "hooks/useGoogleAuth";
import routeNames from "constants/route-names";
import { GoogleAuthStatus } from "constants/google-auth-status";

// Same label everywhere: Sign In, Sign Up and the Guides modal all use this button for both
// creating an account and signing into an existing one, so no single screen-specific verb fits.
const BUTTON_LABEL = "Continue with Google";

interface GoogleAuthButtonProps {
	// Sign-up screens pass their own Terms checkbox state; sign-in has none, so it is left
	// undefined and a fresh Google account always goes through this component's own checkbox.
	termsAccepted?: boolean;
	invitedEmail?: string | null;
	redirectTo?: string;
	onAuthenticated?: () => void;
	// Sign-in shows the "or" divider between the button and the email form; the panels this
	// component renders while resolving a Google account do not need one.
	showDivider?: boolean;
	className?: string;
	// Lets the surrounding screen hide its own email/password form while a Google panel is
	// showing — the panel replaces "sign in with Google", not "sign in with email" too.
	onStatusChange?: (status: GoogleAuthStatus) => void;
}

const GoogleAuthButton = ({
	termsAccepted,
	invitedEmail,
	redirectTo,
	onAuthenticated,
	showDivider = true,
	className = "",
	onStatusChange
}: GoogleAuthButtonProps) => {
	const [panelTermsAccepted, setPanelTermsAccepted] = useState(false);
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [password, setPassword] = useState("");

	const {
		status,
		error,
		pendingProfile,
		pendingLink,
		startGoogleAuth,
		completeAccountCreation,
		completeLinkWithPassword,
		cancel,
		loading
	} = useGoogleAuth({ termsAccepted, invitedEmail, redirectTo, onAuthenticated });

	useEffect(() => {
		onStatusChange?.(status);
	}, [status, onStatusChange]);

	// Only the sign-up screens carry their own checkbox; there, an explicit `false` means the
	// person has not accepted it yet, so the button explains why it is disabled instead of
	// hiding. Sign-in has no checkbox of its own (`termsAccepted` is left undefined there), so
	// this never applies to it — a brand new account from sign-in is asked in the panel below.
	const blockedByUncheckedTerms = termsAccepted === false;

	if (status === GoogleAuthStatus.PENDING_PROFILE) {
		const needsFirstName = !!pendingProfile?.needsFirstName;
		const needsLastName = !!pendingProfile?.needsLastName;
		const needsTerms = !!pendingProfile?.needsTerms;

		return (
			<div className={`gauth-panel ${className}`}>
				<div className="gauth-panel-title">Finish creating your account</div>

				{needsFirstName && (
					<Input
						placeholder="First name"
						className="app-input gauth-panel-input"
						value={firstName}
						onChange={event => setFirstName(event.target.value)}
						allowClear
					/>
				)}

				{needsLastName && (
					<Input
						placeholder="Last name"
						className="app-input gauth-panel-input"
						value={lastName}
						onChange={event => setLastName(event.target.value)}
						allowClear
					/>
				)}

				{needsTerms && (
					<Checkbox
						className="gauth-panel-terms"
						checked={panelTermsAccepted}
						onChange={(event: CheckboxChangeEvent) => setPanelTermsAccepted(event.target.checked)}
					>
						I agree to the&nbsp;
						<Link to="https://highnoteai.com/terms" className="inline-link" rel="noopener" target="_blank">
							Terms and Conditions
						</Link>
					</Checkbox>
				)}

				{error && <Alert message={error} type="error" showIcon className="gauth-panel-error" />}

				<Button
					className="login-layout-button gauth-panel-button"
					loading={loading}
					disabled={
						(needsFirstName && !firstName) || (needsLastName && !lastName) || (needsTerms && !panelTermsAccepted)
					}
					onClick={() => completeAccountCreation(firstName, lastName, panelTermsAccepted)}
				>
					Continue
				</Button>
				<a className="link-label gauth-panel-cancel" onClick={cancel}>
					Cancel
				</a>
			</div>
		);
	}

	if (status === GoogleAuthStatus.PENDING_LINK) {
		return (
			<div className={`gauth-panel ${className}`}>
				<div className="gauth-panel-title">
					You already have an account with {pendingLink?.email}. Enter your password to connect Google.
				</div>

				<Input.Password
					placeholder="Password"
					className="app-input gauth-panel-input"
					value={password}
					autoComplete="current-password"
					onChange={event => setPassword(event.target.value)}
				/>

				{error && <Alert message={error} type="error" showIcon className="gauth-panel-error" />}

				<Button
					className="login-layout-button gauth-panel-button"
					loading={loading}
					disabled={!password}
					onClick={() => completeLinkWithPassword(password)}
				>
					Connect Google
				</Button>
				<div className="gauth-panel-links">
					{/* Opens in a new tab: navigating away here would lose the pending Google
					credential, so the reset happens alongside this tab instead of replacing it. */}
					<Link to={routeNames.FORGOT_PASSWORD} className="link-label" target="_blank" rel="noopener">
						Forgot your password?
					</Link>
					<a className="link-label gauth-panel-cancel" onClick={cancel}>
						Cancel
					</a>
				</div>
			</div>
		);
	}

	const button = (
		<Button
			className="gauth-button"
			loading={loading}
			disabled={blockedByUncheckedTerms}
			onClick={startGoogleAuth}
			icon={<img src={`${process.env.PUBLIC_URL}/assets/images/icons/google-g.svg`} alt="" className="gauth-button-icon" />}
		>
			{BUTTON_LABEL}
		</Button>
	);

	return (
		<div className={`gauth-container ${className}`}>
			{blockedByUncheckedTerms ? (
				// antd buttons stop firing pointer events once disabled, so the tooltip needs a
				// wrapping span to still catch the hover.
				<Tooltip title="Accept the Terms and Conditions first">
					<span>{button}</span>
				</Tooltip>
			) : (
				button
			)}

			{showDivider && <div className="gauth-divider">or</div>}

			{error && <Alert message={error} type="error" showIcon className="gauth-panel-error" />}
		</div>
	);
};

export default GoogleAuthButton;
